import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from './ui';
import { usePullToRefresh } from '../lib/hooks';
import { BRAND } from '../config/brand';
import './AppShell.css';

const TABS = [
  { to: '/', label: 'Today', icon: 'gauge', end: true },
  { to: '/fleet', label: 'Fleet', icon: 'truck' },
  { to: '/inspections', label: 'Records', icon: 'clipboard' },
  { to: '/jobs', label: 'Workshop', icon: 'spanner' },
  { to: '/more', label: 'More', icon: 'more' },
];

/**
 * Page frame: a title bar that can carry a back button and one action, a
 * scrolling body, and the bottom tab bar. The tab bar is hidden on screens
 * that need the full height (running an inspection, signing).
 */
export function Screen({
  title,
  subtitle,
  back,
  action,
  children,
  hideTabs,
  wide,
  onRefresh,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: string | (() => void);
  action?: ReactNode;
  children: ReactNode;
  hideTabs?: boolean;
  wide?: boolean;
  /** Supplied by screens that load data; enables pull-to-refresh. */
  onRefresh?: () => void | Promise<unknown>;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { pull, refreshing, ready, threshold } = usePullToRefresh(onRefresh);

  const goBack = () => {
    if (typeof back === 'function') back();
    else if (typeof back === 'string') navigate(back);
  };

  return (
    <div className={`screen${hideTabs ? ' screen-notabs' : ''}`}>
      <header className="topbar">
        <div className={`topbar-inner${wide ? ' is-wide' : ''}`}>
          {back ? (
            <button type="button" className="btn btn-quiet topbar-back" onClick={goBack} aria-label="Back">
              <Icon name="back" size={20} />
            </button>
          ) : null}
          <div className="topbar-title grow">
            <h1 className="truncate">{title}</h1>
            {subtitle ? <div className="topbar-sub truncate">{subtitle}</div> : null}
          </div>
          {action}
        </div>
      </header>

      {onRefresh ? (
        <div
          className={`ptr${refreshing ? ' is-refreshing' : ''}${ready ? ' is-ready' : ''}`}
          style={{ height: pull, opacity: pull > 0 || refreshing ? 1 : 0 }}
          aria-hidden={!refreshing}
        >
          <span
            className="ptr-spinner"
            style={{ transform: refreshing ? undefined : `rotate(${(pull / threshold) * 270}deg)` }}
          />
          <span className="sr-only" role="status">
            {refreshing ? 'Refreshing' : ''}
          </span>
        </div>
      ) : null}

      {/* Keyed on the path so a route change re-runs the arrival animation. */}
      <main
        key={pathname}
        className={`screen-body${wide ? ' is-wide' : ''}`}
        style={onRefresh && pull ? { transform: `translateY(${pull}px)` } : undefined}
      >
        {children}
      </main>

      {!hideTabs && (
        <>
          <InstallBanner />
          <TabBar />
        </>
      )}
    </div>
  );
}

function TabBar() {
  const { pathname } = useLocation();

  return (
    <nav className="tabbar" aria-label="Main">
      {TABS.map((tab) => {
        // Nested routes should keep their parent tab lit.
        const active = tab.end ? pathname === tab.to : pathname.startsWith(tab.to);
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={`tab${active ? ' is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            <Icon name={tab.icon} size={24} filled={active} />
            <span>{tab.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

/* ------------------------------------------------------------------ *
 * Install banner
 *
 * The app is built to be installed, and iPhone Safari never says so. Shown
 * once, only in Safari on iOS, only when not already running from the home
 * screen, and dismissed for good with one tap.
 * ------------------------------------------------------------------ */

const INSTALL_KEY = 'fc.install.dismissed';

function InstallBanner() {
  const [show, setShow] = useState(false);

  // While the banner is up the page must reserve room for it, or it covers
  // the last row of whatever list is on screen.
  useEffect(() => {
    document.body.toggleAttribute('data-install-banner', show);
    return () => document.body.removeAttribute('data-install-banner');
  }, [show]);

  useEffect(() => {
    try {
      if (localStorage.getItem(INSTALL_KEY)) return;
    } catch {
      return;
    }
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    const standalone =
      (navigator as unknown as { standalone?: boolean }).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
    if (isIOS && isSafari && !standalone) setShow(true);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(INSTALL_KEY, '1');
    } catch {
      /* fine */
    }
    setShow(false);
  };

  return (
    <div className="install" role="status">
      <img className="install-icon" src="./apple-touch-icon.png" alt="" />
      <div className="install-text">
        <strong>Add to your home screen</strong>
        <span>
          Tap <Icon name="share" size={15} /> then <em>Add to Home Screen</em> for the full app.
        </span>
      </div>
      <button type="button" className="btn btn-quiet btn-sm" onClick={dismiss} aria-label="Dismiss">
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}

/** Sign-in and registration frame. */
export function AuthLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="auth-brand">
          <Wordmark />
          <p className="auth-strap">{BRAND.strapline}</p>
        </div>
        <h1 className="auth-title">{title}</h1>
        {children}
      </div>
      <p className="auth-foot tiny dim">
        Inspection records are retained for at least 15 months, as required by DVSA.
      </p>
    </div>
  );
}

export function Wordmark({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    // The two halves are separate spans so they can carry different weights,
    // but the accessible name stays the whole product name rather than being
    // read out as two fragments.
    <div className={`wordmark wordmark-${size}`} role="img" aria-label={BRAND.name}>
      <span className="wordmark-badge" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 17V7h9v10" />
          <path d="M13 10h3.4l3.1 3.3V17H13" />
          <circle cx="8" cy="17.6" r="1.7" />
          <circle cx="16.6" cy="17.6" r="1.7" />
        </svg>
      </span>
      <span className="wordmark-stack" aria-hidden="true">
        <span className="wordmark-text">
          <span className="wordmark-lead">{BRAND.wordmark.lead}</span>
          <span className="wordmark-tail">{BRAND.wordmark.tail}</span>
          <span className="wordmark-dot" />
        </span>
        {/* Dropped at the small size, where the mark has to fit a footer. */}
        {size === 'md' ? <span className="wordmark-sub">{BRAND.wordmark.sub}</span> : null}
      </span>
    </div>
  );
}
